// The document pack: what stands behind an owner's statement figures, listed
// line by line with a checkbox on every row, and a button that zips whatever is
// ticked.
//
// Luyanda has asked for this three times, in the same words each time: on the
// owner file and on the vehicle file, their documents, linked, redacted to only
// what is theirs, and a download that produces a zip, and when they click
// download they can take everything or pick line by line.
//
// ONE COMPONENT, USED TWICE. OwnerDocumentsDialog passes a person id and
// VehicleDocumentsDialog passes a vehicle id. Two copies of this would drift, and
// the half that drifts is the redaction wording.
//
// WHERE THE REDACTION IS. Not here. It is in the shape of the data:
// evidence_extract is built from one vehicle's own rows, so no other owner's
// figures were ever in it, and statements/getOwnerDocumentPack returns a supplier
// document's storage path only when that document names this car alone. This
// component cannot widen either of those, because it never receives the withheld
// path in the first place. What it does own is telling the truth about it: every
// withheld document is listed in the manifest with its reason, never dropped.
import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertCircle, Download, FileArchive, ShieldCheck } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Checkbox } from '../../lib/shadcn/checkbox'
import { Spinner } from '../../lib/shadcn/spinner'
import { useGetOwnerDocumentPack, useGetOwnerDocumentPackSummary } from '../../hooks/backend/statements'
import { useGetEvidenceExtract } from '../../hooks/backend/statements'
import { useCapabilities } from '../../hooks/useCapabilities'
import { formatZar } from '../../utils/statements'
import { buildZip, downloadBlob, type ZipFile } from '../../utils/zip'
import {
  allKeys,
  disabledReason,
  extractCsv,
  extractFileName,
  groupPack,
  groupState,
  manifestCsv,
  packFileName,
  rowKey,
  selectedRows,
  sourceStatus,
  toggleGroup,
  toggleKey,
  type PackExtract,
  type PackRow,
} from '../../utils/documentPack'

type PackSummary = {
  plate: string | null
  documents: number
  months: number
  what_is_in_the_pack: string | null
  document_total_zar: number
}

// Four at a time. The Worker's read limiter already queues these (see
// hooks/requestQueue.ts: the degraded-permission failures are purely a function
// of how many requests are in the air), so firing thirty at once would be
// queued anyway, more slowly, and would spend the whole pool on one download
// while the operator's other panels are still refreshing.
const CONCURRENCY = 4

async function mapLimit<In, Out>(
  items: readonly In[],
  limit: number,
  fn: (item: In) => Promise<Out>,
): Promise<Out[]> {
  const out: Out[] = new Array(items.length)
  let next = 0
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    for (;;) {
      const i = next++
      if (i >= items.length) return
      out[i] = await fn(items[i]!)
    }
  })
  await Promise.all(workers)
  return out
}

export function DocumentPackDownload({
  scope,
  scopeLabel,
}: {
  // Exactly what the two handlers accept. The owner screen holds a person id and
  // the vehicle screen a vehicle id; neither holds an owner id.
  scope: { person_id?: string; vehicle_id?: string }
  scopeLabel: string
}) {
  const pack = useGetOwnerDocumentPack<PackRow[]>()
  const summary = useGetOwnerDocumentPackSummary<PackSummary[]>()
  const extract = useGetEvidenceExtract<PackExtract>()
  const { canViewFinance } = useCapabilities()

  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set())
  const [busy, setBusy] = useState(false)
  const [progress, setProgress] = useState(0)
  const [problem, setProblem] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)

  const scopeKey = `${scope.person_id ?? ''}|${scope.vehicle_id ?? ''}`

  useEffect(() => {
    if (!canViewFinance) return
    if (!scope.person_id && !scope.vehicle_id) return
    setSelected(new Set())
    setProblem(null)
    setNote(null)
    pack.trigger(scope, { skipCache: true })
    summary.trigger(scope, { skipCache: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey, canViewFinance])

  const rows = pack.data ?? []
  const groups = useMemo(() => groupPack(rows), [rows])
  const everyKey = useMemo(() => allKeys(rows), [rows])
  const chosen = useMemo(() => selectedRows(rows, selected), [rows, selected])

  const totals = (summary.data ?? []).reduce(
    (acc, s) => ({
      documents: acc.documents + s.documents,
      zar: acc.zar + (s.document_total_zar ?? 0),
    }),
    { documents: 0, zar: 0 },
  )

  const download = useCallback(async () => {
    setBusy(true)
    setProblem(null)
    setNote(null)
    setProgress(0)

    const failures = new Map<string, string>()
    const files: ZipFile[] = []

    try {
      const fetched = await mapLimit(chosen, CONCURRENCY, async (row) => {
        try {
          // No token means there is nothing to fetch. It cannot happen today
          // (all 1,822 rows carry one) and it must not throw if it ever does.
          if (!row.extract_token) {
            failures.set(row.extract_id, 'no extract token on record')
            return null
          }
          const got = await extract.trigger({ token: row.extract_token }, {})
          if (!got) {
            failures.set(row.extract_id, 'the extract could not be read')
            return null
          }
          return got
        } catch (err) {
          failures.set(row.extract_id, err instanceof Error ? err.message : String(err))
          return null
        } finally {
          setProgress((p) => p + 1)
        }
      })

      chosen.forEach((row, i) => {
        const got = fetched[i]
        if (!got) return
        files.push({ name: extractFileName(row), content: extractCsv(row, got) })
      })

      if (files.length === 0) {
        setProblem(
          `Nothing could be read for this download. ${
            [...failures.values()][0] ?? 'The documents did not load.'
          }`,
        )
        return
      }

      // The manifest lists every SELECTED item, including the ones that failed
      // and the supplier documents that are withheld, so the archive never
      // quietly holds less than was asked for.
      files.unshift({
        name: 'MANIFEST.csv',
        content: manifestCsv(chosen, scopeLabel, failures),
      })

      downloadBlob(packFileName(scopeLabel, files.length - 1), buildZip(files))

      const withheld = chosen.filter((r) => !r.source_shareable).length
      const parts: string[] = [`${files.length - 1} of ${chosen.length} items downloaded.`]
      if (failures.size > 0) {
        parts.push(`${failures.size} could not be read and are listed in the manifest.`)
      }
      if (withheld > 0) {
        parts.push(
          `${withheld} supplier document${withheld === 1 ? '' : 's'} stayed behind because ${
            withheld === 1 ? 'it names' : 'they name'
          } other vehicles on the same account. The manifest says so for each one.`,
        )
      }
      setNote(parts.join(' '))
    } catch (err) {
      setProblem(
        `The download could not be built. ${err instanceof Error ? err.message : String(err)}`,
      )
    } finally {
      setBusy(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosen, scopeLabel])

  if (!canViewFinance) {
    // Never an empty list for a permission problem. An empty list says "this
    // owner has no documents", which is a different and false statement.
    return (
      <section className="border-t border-border pt-3">
        <Header />
        <p className="mt-2 text-xs text-muted-foreground">
          These documents carry the owner’s financial position, and your access does not include
          finance. Ask an admin if you need it.
        </p>
      </section>
    )
  }

  const blocked = disabledReason(rows.length, selected.size)

  return (
    <section className="border-t border-border pt-3">
      <Header />

      {pack.error ? (
        <div className="mt-2 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>The document list could not be loaded. {pack.error}</span>
        </div>
      ) : pack.loading && rows.length === 0 ? (
        <div className="mt-2 flex items-center gap-2 p-4 text-sm text-muted-foreground">
          <Spinner className="h-4 w-4" /> Looking for the documents behind this statement…
        </div>
      ) : rows.length === 0 ? (
        <p className="mt-2 text-xs text-muted-foreground">
          There are no documents on record behind this statement yet, so there is nothing to
          download.
        </p>
      ) : (
        <>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span className="tabular-nums">
              {rows.length} document{rows.length === 1 ? '' : 's'}
            </span>
            {totals.documents > 0 && (
              <span className="tabular-nums">{formatZar(totals.zar)} of charges covered</span>
            )}
            <span className="tabular-nums">{selected.size} selected</span>
          </div>

          <div className="mt-2 flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => setSelected(new Set(everyKey))}
              disabled={busy || selected.size === rows.length}
            >
              Select all
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => setSelected(new Set())}
              disabled={busy || selected.size === 0}
            >
              Clear
            </Button>
            <Button
              size="sm"
              className="h-7 gap-1.5 px-2 text-xs"
              onClick={() => void download()}
              disabled={busy || !!blocked}
            >
              {busy ? <Spinner className="h-3.5 w-3.5" /> : <Download className="h-3.5 w-3.5" />}
              {busy ? `Building… ${progress}/${chosen.length}` : 'Download zip'}
            </Button>
          </div>

          {blocked && !busy && <p className="mt-1.5 text-xs text-muted-foreground">{blocked}</p>}

          {problem && (
            <div className="mt-2 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-2.5 text-xs text-destructive">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{problem}</span>
            </div>
          )}
          {note && !problem && (
            <div className="mt-2 flex items-start gap-2 rounded-md border border-border bg-muted/40 p-2.5 text-xs text-muted-foreground">
              <FileArchive className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{note}</span>
            </div>
          )}

          <div className="mt-2 max-h-[40vh] space-y-3 overflow-y-auto">
            {groups.map((group) => {
              const groupKeys = allKeys(group.rows)
              const state = groupState(selected, groupKeys)
              return (
                <section key={group.key}>
                  <label className="mb-1 flex cursor-pointer items-center gap-2">
                    <Checkbox
                      checked={state === 'some' ? 'indeterminate' : state === 'on'}
                      onCheckedChange={() => setSelected(toggleGroup(selected, groupKeys))}
                      disabled={busy}
                      aria-label={`Select every document for ${group.plate}`}
                    />
                    <span className="font-mono text-xs font-semibold uppercase tracking-wide">
                      {group.plate}
                    </span>
                    <span className="text-xs tabular-nums text-muted-foreground">
                      {group.rows.length} · {formatZar(group.document_total_zar)}
                    </span>
                  </label>

                  <div className="space-y-2 pl-6">
                    {group.months.map((month) => {
                      const monthKeys = allKeys(month.rows)
                      const monthState = groupState(selected, monthKeys)
                      return (
                        <div key={month.key}>
                          <label className="flex cursor-pointer items-center gap-2">
                            <Checkbox
                              checked={
                                monthState === 'some' ? 'indeterminate' : monthState === 'on'
                              }
                              onCheckedChange={() => setSelected(toggleGroup(selected, monthKeys))}
                              disabled={busy}
                              aria-label={`Select every document for ${group.plate} in ${month.month}`}
                            />
                            <span className="text-xs font-medium text-muted-foreground">
                              {month.month}
                            </span>
                          </label>
                          <ul className="mt-1 overflow-hidden rounded-md border border-border">
                            {month.rows.map((row) => (
                              <li
                                key={rowKey(row)}
                                className="flex items-start gap-2 border-b border-border px-2 py-1.5 last:border-b-0"
                              >
                                <Checkbox
                                  className="mt-0.5"
                                  checked={selected.has(rowKey(row))}
                                  onCheckedChange={() => setSelected(toggleKey(selected, rowKey(row)))}
                                  disabled={busy}
                                  aria-label={`Select ${row.line_label ?? row.line_code} for ${
                                    row.plate ?? 'this vehicle'
                                  } in ${row.month ?? row.period_start}`}
                                />
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-baseline justify-between gap-2">
                                    <span className="truncate text-xs font-medium">
                                      {row.line_label ?? row.line_code}
                                    </span>
                                    <span className="shrink-0 text-xs tabular-nums">
                                      {formatZar(row.document_zar)}
                                    </span>
                                  </div>
                                  <p className="truncate text-[11px] text-muted-foreground">
                                    {row.document_title ?? row.supplier ?? 'Evidence extract'}
                                  </p>
                                  {!row.source_shareable && (
                                    <p className="text-[11px] text-muted-foreground/80">
                                      {sourceStatus(row)}
                                    </p>
                                  )}
                                  {row.is_draft && (
                                    <p className="text-[11px] text-muted-foreground/80">
                                      Draft statement
                                    </p>
                                  )}
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )
                    })}
                  </div>
                </section>
              )
            })}
          </div>
        </>
      )}
    </section>
  )
}

function Header() {
  return (
    <>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Documents behind the statement
      </h3>
      <p className="mt-0.5 flex items-start gap-1.5 text-[11px] text-muted-foreground">
        <ShieldCheck className="mt-0.5 h-3 w-3 shrink-0" />
        <span>
          Each item is built from this vehicle’s own rows, so another owner’s figures were never in
          it. A supplier document that names other vehicles stays behind and is listed in the
          manifest with the reason.
        </span>
      </p>
    </>
  )
}
